/**
 * KombiJourney — a multi-stage journey drawn as a road, with the Kombi at the stage you are at.
 *
 * ONE VAN THAT MOVES, NOT ONE VAN PER STAGE. That is the whole design. Rendering a vehicle per step
 * makes a row of near-identical drawings, which reads as a repeated asset rather than as progress;
 * rendering one van whose POSITION is the state makes the metaphor do the work, and it is also how the
 * thing being depicted actually behaves. Progress is therefore carried by three things — where the van
 * is, which milestones are filled, and (at the end) whether the van has parked — never by colour alone.
 *
 * THE ROAD IS THE ILLUSTRATION'S ROAD: --kombi-sand with near-white centre dashes, not a filled
 * progress track. See kombiJourney.css for why a track that fills up would be wrong here.
 *
 * ARRIVAL IS A DRAWN STATE, NOT AN ABSENCE OF MOTION. `complete` parks the van beside a parking sign
 * (Kombi's `parked`), because a still van and a driving van are otherwise the same picture — without
 * the sign, a finished journey looks like a stalled one. The final milestone is then NOT drawn: the
 * sign is the marker for that stop, and two markers in one place reads as a mistake.
 *
 * ACCESSIBILITY. The visual track is entirely aria-hidden; the accessible version is an ordered list
 * of the stop names with `aria-current="step"` on the one you are at — the same shape the design's own
 * StepPills uses, so assistive tech gets a consistent story across the app. `showLabels={false}` hides
 * the names VISUALLY ONLY (they stay in the list via .sr-only), for hosts whose own copy already states
 * the position in words. Do not pass it on a surface where nothing else names the stage.
 */
import Kombi from './Kombi';
import './kombiJourney.css';
import type { CSSProperties } from 'react';

export interface KombiJourneyProps {
  /** The stops, in order. Two or more; the labels are the accessible names of the steps. */
  stops: readonly string[];
  /** 0-based index of the stop the traveller is AT. Clamped into range rather than trusted. */
  current: number;
  /**
   * The journey is finished. Parks the van beside a parking sign at the last stop and stops the wheels.
   * Note this is separate from `current === stops.length - 1`: being AT the last stop and having
   * FINISHED are different states in at least one host (a tool sits at "Pending" for as long as review
   * takes), so the caller decides.
   */
  complete?: boolean;
  /** Accessible name for the list. Required — an unnamed progress indicator is a puzzle. */
  label: string;
  /** Render the stop names visibly. False keeps them in the list for AT but hides them on screen. */
  showLabels?: boolean;
  /**
   * The van's height in px, which is what the whole indicator scales from. Omit for the CSS default
   * (44). Lower it on a surface where vertical space is contested — the upload wizard passes 30,
   * because it sits in a fixed-height modal whose body pays for every pixel the header takes.
   */
  vanHeight?: number;
  className?: string;
}

type StopState = 'done' | 'current' | 'todo';

export function KombiJourney({
  stops,
  current,
  complete = false,
  label,
  showLabels = true,
  vanHeight,
  className = '',
}: KombiJourneyProps) {
  const count = stops.length;
  // A journey with nothing to travel is not a journey. Render nothing rather than a bare road.
  if (count === 0) return null;

  // Clamped, not trusted: `current` is derived from server state at two of the three call sites (a
  // publication status, a step count), and an out-of-range value should degrade to an end of the road
  // rather than position the van outside its own container.
  const at = Math.min(Math.max(Math.round(current) || 0, 0), count - 1);

  /** Half a column — the gap from each edge to the first and last milestone's centre. */
  const inset = 50 / count;
  /** Any stop's centre, as a percentage of the track's width. */
  const centre = (i: number) => ((i + 0.5) / count) * 100;

  const stateOf = (i: number): StopState => (i < at ? 'done' : i === at ? 'current' : 'todo');

  const trackVars = {
    '--kj-count': String(count),
    '--kj-inset': `${inset}%`,
    '--kj-x': `${centre(at)}%`,
    ...(vanHeight !== undefined ? { '--kj-van-h': `${vanHeight}px` } : {}),
  } as CSSProperties;

  return (
    <div className={`tb-kj ${className}`.trim()} style={trackVars}>
      {/* THE PICTURE — wholly decorative. Every fact it shows is in the list below it. */}
      <div className="tb-kj__track" aria-hidden="true">
        <span className="tb-kj__road" />

        {stops.map((stop, i) =>
          // The last milestone is the parking sign's job once the journey is complete.
          complete && i === count - 1 ? null : (
            <span
              key={stop}
              className="tb-kj__dot"
              data-state={stateOf(i)}
              style={{ '--kj-dot-x': `${centre(i)}%` } as CSSProperties}
            />
          ),
        )}

        <span className="tb-kj__van">
          {/* `bus` because a landscape at 44px tall would be illegible, and because the road here is
              the component's own. Wheels turn while travelling and stop on arrival. */}
          <Kombi variant="bus" motion={complete ? 'still' : 'drive'} parked={complete} />
        </span>
      </div>

      {/* THE ACCESSIBLE VERSION, and the visible labels when the host wants them. */}
      <ol className="tb-kj__stops" aria-label={label}>
        {stops.map((stop, i) => (
          <li
            key={stop}
            className="tb-kj__stop"
            data-state={stateOf(i)}
            {...(i === at ? { 'aria-current': 'step' as const } : {})}
          >
            <span className={showLabels ? undefined : 'sr-only'}>{stop}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
